<template>
  <div v-if="Object.keys(map).length">
    <div class="map-sidebar-overlay d-flex flex-row">
      <div class="map-sidebar flex-grow-1">
        <div class="px-4 mt-2">
          <div class="text-h6">Explore Metadata</div>
        </div>
        <!--
        height calculation below allows us to keep the title
        and reset/create footer buttons sticky while providing
        a scroll bar if the panels expanded are greater than
        the available height

        64px = app header height
        48px = title height + 8px top margin
        60px = reset/explore footer height
        20px = padding for top and bottom
      -->
        <div
          style="
            max-height: calc(100vh - (64px + 48px + 60px + 20px));
            overflow: auto;
          "
          class="px-4"
        >
          <v-expansion-panels
            v-model="panel"
            multiple
            accordion
            tile
            focusable
            v-show="mounted && !metadataLoaded"
          >
            <aoi-panel
              ref="aoiPanel"
              :map="map"
              @mounted="onAoiMounted"
              :highlight="true"
            />
          </v-expansion-panels>
        </div>
        <div class="map-legend-container pa-4">
          <div :class="'d-flex justify-space-between'">
            <v-tooltip v-if="!metadataLoaded" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  id="reset-button"
                  :small="metadataLoaded"
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  @click="onReset()"
                  :disabled="!hasAois"
                >
                  Reset
                </v-btn>
              </template>
              <span>Remove all AOI's</span>
            </v-tooltip>
            <v-tooltip v-if="metadataLoaded" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  id="edit-button"
                  :small="metadataLoaded"
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  @click="edit()"
                  :disabled="!aoi"
                >
                  Edit
                </v-btn>
              </template>
              <span>Select Imagery based on the drawn AOI</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  id="explore-button"
                  :small="metadataLoaded"
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  outlined
                  @click="loadRegionMetadata()"
                  :disabled="!isExploreDisabled()"
                >
                  Explore Metadata
                </v-btn>
              </template>
              <span>Load the metadata based on the drawn AOI</span>
            </v-tooltip>
          </div>
          <v-expansion-panels
            id="cell-metadata-panel"
            multiple
            accordion
            tile
            focusable
            v-if="metadataLoaded && selected_feature"
            class="mt-4"
            v-model="cellPanel"
          >
            <v-expansion-panel>
              <v-expansion-panel-header>Cell Metadata</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="mt-4 text-caption">
                  <cell-metadata :feature="selected_feature"></cell-metadata>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-row class="mt-1" v-if="metadataLoaded" align="center" no-gutters>
            <v-col
              cols="6"
              class="flex-grow-0 flex-shrink-1 text-caption text-nowrap"
              ><v-icon class="mr-1" color="white">mdi-map-legend</v-icon>Color
              Metadata By</v-col
            >
            <v-col>
              <v-select
                v-model="legendStyle"
                :items="legendStyles"
                item-text="label"
                item-value="value"
                menu-props="auto"
                label="Legend Style"
                hide-details
                single-line
                class="flex-grow-1 flex-shrink-0"
                style="
                  min-width: 100px;
                  max-width: 100%;
                  margin-top: 0;
                  padding-top: 0;
                "
                @change="onLegendStyleChange"
              ></v-select>
            </v-col>
          </v-row>
          <div
            v-if="Array.isArray(legendEntries) && legendEntries.length > 0"
            class="text-caption"
          >
            <div v-if="metadataLoaded" class="pb-2">
              <div
                v-for="entry in legendEntries"
                :key="entry.text"
                class="d-flex w-100"
                style="justify-content: space-between; align-items: center"
              >
                <div
                  class="legend-swatch"
                  :style="`background-color: ${entry.color}`"
                ></div>
                <div>{{ entry.text }}</div>
              </div>
            </div>
          </div>
          <div v-if="metadataLoaded" class="d-flex justify-space-between">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  id="reset-button"
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  @click="onReset()"
                  :disabled="!hasAois"
                >
                  Reset
                </v-btn>
              </template>
              <span>Remove AOI's and metadata</span>
            </v-tooltip>
            <v-tooltip v-if="metadataLoaded" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  id="build-selection-button"
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  outlined
                  @click="createSelection()"
                  :disabled="!aoi"
                >
                  Select Imagery
                </v-btn>
              </template>
              <span>Select Imagery based on the drawn AOI</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="map-search-container px-4">
      <div id="geocoder-container"></div>
    </div>
    <div v-show="loading" class="map-loading-overlay">
      <div class="text-center pa-2">
        <spinner>Loading metadata...</spinner>
      </div>
    </div>
    <confirm-modal
      ref="confirmResetModal"
      title="Reset?"
      confirm-btn-text="Yes, Clear"
      cancel-btn-text="No, Cancel"
    >
      <div style="max-width: 350px">
        Clearing will remove all AOI's and metadata. Are you sure you want to
        continue?
      </div>
    </confirm-modal>
  </div>
</template>

<script>
import AoiPanel from "@/components/aoiPanel/AOIPanel";
import Spinner from "@/components/shared/Spinner";
import ConfirmModal from "@/components/shared/ConfirmModal";
import mapUtils from "@/utils/map";
import axios from "axios";
import turfBbox from "@turf/bbox";
import turfContains from "@turf/boolean-contains";
import turfEnvelope from "@turf/envelope";
import * as turf from "@turf/helpers";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import CellMetadata from "./CellMetadata.vue";
const uiStore = createNamespacedHelpers("ui");

export default {
  props: {
    map: {
      type: Object,
      required: true,
    },
    map_loaded: {
      type: Boolean,
      required: true,
    },
  },
  mixins: [mapUtils],
  components: { Spinner, AoiPanel, ConfirmModal, CellMetadata },
  data() {
    const legendStyles = [
      { label: "Tile Depth", value: "tile_count" },
      { label: "> 75% Tile Coverage", value: "data_percentage_75" },
      { label: "> 75% Cloud Free", value: "cloud_free_percentage_75" },
      { label: "Most Recent Date", value: "max_date_timestamp" },
    ];
    const legendEntries = this.getLegendStyle(legendStyles[0].value).entries;
    const empty_features = {
      type: "FeatureCollection",
      features: [],
    };

    return {
      loading: false,
      metadataSourceName: "Metadata",
      drawnPolygonSourceName: "Drawn Polygon",
      cellTextLayerName: "Cell Text Values",
      hoveredSquareId: null,
      legendStyle: legendStyles[0],
      legendStyles,
      legendEntries,
      textPropertyName: legendStyles[0].value,
      aoi: null,
      mounted: false,
      panel: [],
      cellPanel: [0],
      metadataLoaded: false,
      cell_id: null,
      selected_feature: null,
      empty_features,
    };
  },
  computed: {
    hasAois() {
      if (this.$refs.aoiPanel) {
        return this.$refs.aoiPanel.aois.length > 0;
      }

      return false;
    },
  },
  mounted() {
    this.mounted = true;
    this.initMap();
  },
  beforeDestroy() {
    // remove everything we have drawn
    const sourcesToRemove = [
      {
        name: this.metadataSourceName,
        layersToRemove: [
          this.metadataSourceName,
          `${this.metadataSourceName} Outline`,
          this.cellTextLayerName,
        ],
      },
      {
        name: this.drawnPolygonSourceName,
        layersToRemove: [
          `${this.drawnPolygonSourceName} Fill`,
          `${this.drawnPolygonSourceName} Outline`,
        ],
      },
      { name: "cell-extent", layersToRemove: ["hightlight-cell-bounds"] },
      { name: "hover-extent", layersToRemove: ["hover-cell-bounds"] },
    ];
    sourcesToRemove.forEach((s) => {
      const source = this.map.getSource(s.name);

      // only remove the source and layers if there is a source
      if (source) {
        this.map.removeSourceLayers(s.name, s.layersToRemove);

        // null out data
        source.setData({ type: "FeatureCollection", features: [] });
      }
    }, this);

    // set draw mode back to default simple_select
    this.map.setDrawMode("simple_select");
    this.mounted = false;
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  methods: {
    onAoiMounted() {
      // need to delay draw rectangle mode to let everything initialize correctly
      setTimeout(
        (() => {
          // set the draw mode
          this.map.setDrawMode("draw_rectangle");
        }).bind(this),
        400
      );
    },
    initMap() {
      if (this.map_loaded) {
        this.$log.debug("Adding sources and layers to the map...");
        // eslint-disable-next-line
        const me = this;
        const map = this.map;

        // eslint-disable-next-line
        const mapboxgl = require("mapbox-gl");
        const metadataOutlineSourceName = `${this.metadataSourceName} Outline`;

        map.addSourceLayers(
          this.metadataSourceName,
          {
            type: "geojson",
            data: null,
          },
          [
            {
              id: this.metadataSourceName,
              type: "fill",
              source: this.metadataSourceName,
              paint: {
                "fill-color": me.getLegendStyle(me.legendStyle.value).fillColor,
                "fill-opacity": [
                  "case",
                  ["boolean", ["feature-state", "hover"], false],
                  0.9,
                  0.8,
                ],
              },
            },
            {
              id: metadataOutlineSourceName,
              type: "line",
              source: this.metadataSourceName,
              layout: {},
              paint: {
                "line-color": "rgba(204, 204, 204, 0.8)", // default to 80% opaque gray
                "line-width": 3,
              },
            },
            {
              minzoom: 8,
              id: this.cellTextLayerName,
              type: "symbol",
              source: this.metadataSourceName,
              layout: {
                "text-field": ["get", this.textPropertyName],
                "text-variable-anchor": ["center"],
                "text-radial-offset": 0.0,
                "text-justify": "auto",
                "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
              },
              paint: {
                "text-color": "rgba(255, 255, 255, 1.0)",
                "text-halo-color": "rgba(0, 0, 0, 1.0)",
                "text-halo-width": 1,
              },
            },
          ]
        );

        const showCellMetadata = (feature) => {
          this.selected_feature = feature;
          this.cellPanel = [0];
        };

        const drawnFillLayerId = `${this.drawnPolygonSourceName} Fill`;
        map.addSourceLayers(
          this.drawnPolygonSourceName,
          {
            type: "geojson",
            data: null,
          },
          [
            {
              id: drawnFillLayerId,
              type: "fill",
              source: this.drawnPolygonSourceName,
              paint: {
                "fill-color": "#FFFF00",
                "fill-outline-color": "#FFFF00",
                "fill-opacity": 0.05,
              },
            },
            {
              id: `${this.drawnPolygonSourceName} Outline`,
              type: "line",
              source: this.drawnPolygonSourceName,
              layout: {},
              paint: {
                "line-color": "#FFFF00",
                "line-width": 3,
              },
            },
          ],
          [
            // Handle click event on a grid item
            {
              type: "click",
              layerId: this.metadataSourceName,
              handler: (e) => {
                const map = e.target;
                map.getCanvas().style.cursor = "";
                showCellMetadata(e.features[0]);
                if (map.getSource("cell-extent")) {
                  map.getSource("cell-extent").setData(e.features[0]);
                } else {
                  map.addSource("cell-extent", {
                    type: "geojson",
                    data: e.features[0],
                  });
                  map.addLayer({
                    id: "hightlight-cell-bounds",
                    type: "line",
                    source: "cell-extent",
                    layout: {},
                    paint: {
                      "line-width": 5,
                      "line-color": "#FCAF17", // maxar primary gold color
                    },
                  });
                }
                this.cell_id = e.features[0].properties.id;
              },
            },
            // Highlight grid on hover as well as change the cursor to a pointer
            {
              type: "mousemove",
              layerId: this.metadataSourceName,
              handler: (e) => {
                const map = e.target;

                if (me.hoveredSquareId) {
                  map.setFeatureState(
                    { source: me.metadataSourceName, id: me.hoveredSquareId },
                    { hover: false }
                  );
                }

                if (!isNaN(e.features[0].id)) {
                  map.getCanvas().style.cursor = "pointer";

                  me.hoveredSquareId = e.features[0].id;
                  map.setFeatureState(
                    { source: me.metadataSourceName, id: me.hoveredSquareId },
                    { hover: true }
                  );

                  if (map.getSource("hover-extent")) {
                    map.getSource("hover-extent").setData(e.features[0]);
                  } else {
                    map.addSource("hover-extent", {
                      type: "geojson",
                      data: e.features[0],
                    });
                    map.addLayer({
                      id: "hover-cell-bounds",
                      type: "line",
                      source: "hover-extent",
                      layout: {},
                      paint: {
                        "line-width": 3,
                        "line-color": "#026AE3", // maxar secondary link color
                      },
                    });
                  }
                } else {
                  map.getCanvas().style.cursor = "";

                  if (map.getSource("hover-extent")) {
                    map.getSource("hover-extent").setData(this.empty_features);
                  }
                }
              },
            },
            // Remove highlight when we leave a grid item as well as clear the cursor style
            {
              type: "mouseleave",
              layerId: this.metadataSourceName,
              handler: (e) => {
                const map = e.target;
                map.getCanvas().style.cursor = "";

                if (me.hoveredSquareId) {
                  map.setFeatureState(
                    { source: me.metadataSourceName, id: me.hoveredSquareId },
                    { hover: false }
                  );
                }

                if (map.getSource("hover-extent")) {
                  map.getSource("hover-extent").setData(this.empty_features);
                }

                me.hoveredSquareId = null;
              },
            },
          ],
          this.cellTextLayerName
        );

        // need to delay setting draw mode to let everything initialize correctly
        setTimeout(
          (() => {
            this.map.setDrawMode("draw_rectangle");
            this.panel = [0];
          }).bind(this),
          400
        );
      } else {
        setTimeout(this.initMap, 200);
      }
    },

    isExploreDisabled() {
      if (
        this.$refs.aoiPanel &&
        this.$refs.aoiPanel.aois.length > 0 &&
        this.$refs.aoiPanel.aoisValid &&
        !this.metadataLoaded
      ) {
        return true;
      } else {
        return false;
      }
    },
    setFeatureCollection(featureCollection) {
      this.aoi = null;
      this.metadataLoaded = false;
      if (Array.isArray(featureCollection.features)) {
        const cleanAois = this.getCleanAois(featureCollection.features);
        this.$refs.aoiPanel.aois = [...cleanAois];
      } else {
        this.$refs.aoiPanel.aois = [];
      }
    },
    async loadRegionMetadata() {
      try {
        const union = this.getIntersects(true);
        this.map.getSource(this.drawnPolygonSourceName).setData(union);
        this.aoi = union;

        this.$emit("set_drawn_feature", {
          aoi: this.empty_features,
        });

        this.$emit("set_draw_tools", {
          draw: false,
        });

        this.loading = true;

        const metadata_url = `https://${process.env.VUE_APP_ARD_DOMAIN}/metadata/cells/summary`;
        const response = await axios.post(metadata_url, {
          intersects: union.geometry,
        });

        let metadata_bounds = null;

        if (response.data && Array.isArray(response.data.features)) {
          if (response.data.features.length > 0) {
            // check if we should register the area
            metadata_bounds = response.data.features[0];

            if (response.data.features.length > 1) {
              // get area surronding all returned cells
              const feats = turf.featureCollection(response.data.features);
              metadata_bounds = turfEnvelope(feats);
            }
          }
          // if metadata returned doesnt cover the entire aoi, register the aoi
          const union_bounds = turfEnvelope(union);
          if (
            !metadata_bounds ||
            !turfContains(metadata_bounds, union_bounds)
          ) {
            const register_url = `https://${process.env.VUE_APP_ARD_DOMAIN}/metadata/cells/summary/register`;
            await axios.post(register_url, {
              intersects: union.geometry,
            });
            // give backend time to register
            await this.sleep(5000);
            // check for results
            const response = await axios.post(metadata_url, {
              intersects: union.geometry,
            });

            this.mapMetadata(response, union);
          } else {
            this.mapMetadata(response, union);
          }
        }
      } catch (err) {
        console.error(err);
        this.SHOW_SNACKBAR({
          text: `Failed to load region metadata: ${err.toString()}`,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    mapMetadata(response, union) {
      const data = response.data;
      this.metadataLoaded = true;
      this.selected_feature = null;
      this.cell_id = null;

      let id = 1;
      // add number id's to the features so we can do hover states, note the string id's are not honored for a feature id on the map
      data.features.forEach((f) => {
        f.id = id;

        // add date prop for display
        f.properties.max_date_locale_str = new Date(
          f.properties.max_date_timestamp * 1000
        ).toLocaleDateString(
          window.navigator.userLanguage || window.navigator.language,
          { month: "numeric", year: "numeric" }
        );
        id++;
      });

      this.map.getSource(this.metadataSourceName).setData(data);

      // zoom to fit the bounds of the metadata or the polygon if we don't have metadata
      const newBounds = turfBbox(
        response.data.features.length > 0 ? response.data : union
      );

      // zoom to the extents of the selected grid item
      this.panMap(this.map, newBounds);

      // set draw mode back to simple select
      this.map.setDrawMode("simple_select");

      if (response.data.features.length === 0) {
        this.SHOW_SNACKBAR({
          text: `The AOI you specified does not have any cell metadata.  Please create an AOI in a different location.`,
          color: "info",
        });
      }
    },
    getFillTileCount() {
      const legend_entries = [
        { color: "rgba(247, 252, 245, .1)", text: "0-50" },
        { color: "rgba(199, 233, 192, .3)", text: "50-100" },
        { color: "rgba(116, 196, 118, .5)", text: "100-250" },
        { color: "rgba(35, 139, 69, .75)", text: "250-500" },
        { color: "rgba(0, 68, 27, .9)", text: "> 500" },
      ];

      return {
        entries: legend_entries,
        fillColor: [
          "interpolate",
          ["linear"],
          ["get", "tile_count"],
          0,
          ["to-color", legend_entries[0].color],
          50,
          ["to-color", legend_entries[1].color],
          100,
          ["to-color", legend_entries[2].color],
          250,
          ["to-color", legend_entries[3].color],
          500,
          ["to-color", legend_entries[4].color],
        ],
      };
    },
    getFillDataPercentage75Count() {
      const legend_entries = [
        { color: "rgba(255, 245, 235, .1)", text: "0-50" },
        { color: "rgba(253, 208, 162, .3)", text: "50-100" },
        { color: "rgba(253, 141, 60, .5)", text: "100-250" },
        { color: "rgba(217, 72, 1, .75)", text: "250-500" },
        { color: "rgba(127, 39, 4, .9)", text: "> 500" },
      ];

      return {
        entries: legend_entries,
        fillColor: [
          "interpolate",
          ["linear"],
          ["get", "data_percentage_75"],
          0,
          ["to-color", legend_entries[0].color],
          50,
          ["to-color", legend_entries[1].color],
          100,
          ["to-color", legend_entries[2].color],
          250,
          ["to-color", legend_entries[3].color],
          500,
          ["to-color", legend_entries[4].color],
        ],
      };
    },

    getFillCloudFree75Count() {
      const legend_entries = [
        { color: "rgba(247, 251, 255, .1)", text: "0-50" },
        { color: "rgba(198, 219, 239, .3)", text: "50-100" },
        { color: "rgba(107, 174, 214, .5)", text: "100-250" },
        { color: "rgba(33, 113, 181, .75)", text: "250-500" },
        { color: "rgba(8, 48, 107, .9)", text: "> 500" },
      ];

      return {
        entries: legend_entries,
        fillColor: [
          "interpolate",
          ["linear"],
          ["get", "cloud_free_percentage_75"],
          0,
          ["to-color", legend_entries[0].color],
          50,
          ["to-color", legend_entries[1].color],
          100,
          ["to-color", legend_entries[2].color],
          250,
          ["to-color", legend_entries[3].color],
          500,
          ["to-color", legend_entries[4].color],
        ],
      };
    },
    getFillMaxDate() {
      const legend_entries = [
        { color: "rgba(103, 0, 31, .2)", text: "older than 2 years" },
        { color: "rgba(178, 24, 43, .5)", text: "between 1 and 2 years" },
        {
          color: "rgba(244, 165, 130, .5)",
          text: "between 6 months and 1 year",
        },
        { color: "rgba(247, 247, 247, .5)", text: "between 3 and 6 months" },
        { color: "rgba(146, 197, 222, .7)", text: "between 2 and 3 months" },
        { color: "rgba(33, 102, 172, .7)", text: "between 1 and 2 months" },
        { color: "rgba(5, 48, 97, .9)", text: "newer than 1 month" },
      ];

      function getTimestamp(interval) {
        return new Date().setDate(new Date().getDate() - interval) / 1000;
      }

      return {
        entries: legend_entries,
        fillColor: [
          "interpolate",
          ["linear"],
          ["get", "max_date_timestamp"],
          1237435200, // Thu Jan 15 1970 00:43:55 GMT-0700 (Mountain Standard Time)
          ["to-color", legend_entries[0].color],

          // 2 years
          getTimestamp(730),
          ["to-color", legend_entries[1].color],

          // 1 year
          getTimestamp(365),
          ["to-color", legend_entries[2].color],

          // 180 days
          getTimestamp(180),
          ["to-color", legend_entries[3].color],

          // 90 days
          getTimestamp(90),
          ["to-color", legend_entries[4].color],

          // 60 days
          getTimestamp(60),
          ["to-color", legend_entries[5].color],

          // 30 days
          getTimestamp(30),
          ["to-color", legend_entries[6].color],
        ],
      };
    },
    getLegendStyle(style) {
      switch (style) {
        case "tile_count":
          return this.getFillTileCount();
        case "data_percentage_75":
          return this.getFillDataPercentage75Count();
        case "cloud_free_percentage_75":
          return this.getFillCloudFree75Count();
        case "max_date_timestamp":
          return this.getFillMaxDate();
      }

      return this.getFillTileCount();
    },
    onLegendStyleChange(style) {
      const legendStyle = this.getLegendStyle(style);
      this.legendEntries = legendStyle.entries;
      this.map.map.setPaintProperty(
        this.metadataSourceName,
        "fill-color",
        legendStyle.fillColor
      );

      this.textPropertyName = style;
      this.map.map.setLayoutProperty(this.cellTextLayerName, "text-field", [
        "get",
        style === "max_date_timestamp" ? "max_date_locale_str" : style,
      ]);
    },
    createSelection() {
      this.$router.push({
        name: "Select",
        params: this.aoi,
      });
    },
    edit() {
      this.$emit("set_draw_tools", {
        draw: true,
      });
      this.map.getSource(this.metadataSourceName).setData(this.empty_features);
      this.map
        .getSource(this.drawnPolygonSourceName)
        .setData(this.empty_features);
      if (this.map.getSource("cell-extent")) {
        this.map.getSource("cell-extent").setData(this.empty_features);
      }
      if (this.map.getSource("hover-extent")) {
        this.map.getSource("hover-extent").setData(this.empty_features);
      }
      this.metadataLoaded = false;

      this.$emit("set_drawn_feature", {
        aoi: this.aoi,
      });
    },
    onReset() {
      this.$refs.confirmResetModal.show().then((yes) => {
        if (yes) {
          this.selected_feature = null;
          this.cell_id = null;
          this.$emit("set_draw_tools", {
            draw: true,
          });
          this.map
            .getSource(this.metadataSourceName)
            .setData(this.empty_features);
          this.map
            .getSource(this.drawnPolygonSourceName)
            .setData(this.empty_features);
          if (this.map.getSource("cell-extent")) {
            this.map.getSource("cell-extent").setData(this.empty_features);
          }
          if (this.map.getSource("hover-extent")) {
            this.map.getSource("hover-extent").setData(this.empty_features);
          }
          this.metadataLoaded = false;
          this.map.removeAllDrawnFeatures();
          this.$refs.aoiPanel.aois = [];

          this.$nextTick(() => {
            this.map.setDrawMode("draw_rectangle");
          });
        }
      });
    },
    sleep(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.map-top-left-header {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
